import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AdvancedFilterButton,
  AdvancedFilterPanel,
  FilterPanelCollapse,
} from '@/components/common/AdvancedFilter';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { DataTable } from '@/components/common/DataTable';
import { DateRangeBadge } from '@/components/common/DateRangeBadge';
import { ErrorState } from '@/components/common/ErrorState';
import { PageHeader } from '@/components/layout/PageHeader';
import { FIT_TO_SCREEN_PAGE_CLASS } from '@/components/layout/page.constants';
import { promoItemsQueryOptions } from '@/features/promotion/api/promotion.api';
import { zonalManagersQueryOptions } from '@/features/users/api/users.api';
import { useInfiniteTableQuery } from '@/hooks/useInfiniteTableQuery';
import { getDateFromToFilterFields, getZonalManagerFilterField } from '@/utils/filterFields';
import { getUpcomingSearch } from '@/utils/upcomingSearch';
import { donationBatchesInfiniteQueryOptions } from '../api/marketPromotionP2.api';
import { DonationMembersDialog } from '../components/DonationMembersDialog';
import { DonationPublishDialog } from '../components/DonationPublishDialog';
import { getDonationBatchColumns } from '../components/MpP2Columns';
import { DONATION_ITEM_LOOKUP_PARAMS } from '../constants/marketPromotion.constants';
import { useDonationBatchFilters, type DonationBatchPanel } from '../hooks/useDonationBatchFilters';
import { useConfirmDonationBatch, useDisburseDonationBatch } from '../hooks/useMpP2Mutations';
import type { DonationBatch } from '../types/marketPromotionP2.types';
import { getDonationItemFilterField } from '../utils/mpFilterFields';

interface DonationBatchPageProps {
  panel: DonationBatchPanel;
  title: string;
}

export function DonationBatchPage({ panel, title }: DonationBatchPageProps) {
  const controller = useDonationBatchFilters(panel);
  const { search } = controller;

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [openBatch, setOpenBatch] = useState<DonationBatch | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [confirming, setConfirming] = useState<DonationBatch | null>(null);
  const [disbursing, setDisbursing] = useState<DonationBatch | null>(null);

  const stage = panel === 'publish' ? 'sales' : 'accounts';
  const { rows, isLoading, loadError, refetch, loadMoreProps } = useInfiniteTableQuery(
    donationBatchesInfiniteQueryOptions(stage, search),
  );
  const { data: items } = useQuery(promoItemsQueryOptions(DONATION_ITEM_LOOKUP_PARAMS));
  const { data: zonalManagers } = useQuery(zonalManagersQueryOptions());

  const confirm = useConfirmDonationBatch(() => setConfirming(null));
  const disburse = useDisburseDonationBatch(() => setDisbursing(null));

  const action =
    panel === 'publish'
      ? {
          label: 'Confirm',
          onClick: setConfirming,
          when: (batch: DonationBatch) => batch.isConfirm !== true && !batch.isDisburse,
        }
      : {
          label: 'Disburse',
          onClick: setDisbursing,
          when: (batch: DonationBatch) => batch.isConfirm === true && !batch.isDisburse,
        };

  return (
    <div className={FIT_TO_SCREEN_PAGE_CLASS}>
      <PageHeader
        title={title}
        titleAddon={<DateRangeBadge dateFrom={search.dateFrom} dateTo={search.dateTo} />}
        search={getUpcomingSearch('Batch, Donation Item')}
        actions={
          <AdvancedFilterButton
            isOpen={isFilterOpen}
            activeCount={controller.activeFilterCount}
            onClick={() => setIsFilterOpen((wasOpen) => !wasOpen)}
          />
        }
        createAction={
          panel === 'publish'
            ? { label: 'Publish', mobileLabel: 'Publish', onClick: () => setPublishing(true) }
            : undefined
        }
      />

      <FilterPanelCollapse isOpen={isFilterOpen}>
        <AdvancedFilterPanel
          fields={[
            ...getDateFromToFilterFields(),
            getZonalManagerFilterField(zonalManagers),
            getDonationItemFilterField(items?.data),
          ]}
          values={controller.filterValues}
          defaultValues={controller.defaultFilterValues}
          onChange={controller.handleFilterChange}
        />
      </FilterPanelCollapse>

      {loadError ? (
        <ErrorState
          error={loadError}
          fallbackTitle="Could not load the batches"
          onRetry={() => void refetch()}
          fill
        />
      ) : (
        <DataTable
          data={rows}
          columns={getDonationBatchColumns(setOpenBatch, action)}
          isLoading={isLoading}
          className="lg:flex-1"
          stickyColumnId="batch"
          emptyMessage="No donation batch has ever been published"
          {...loadMoreProps}
        />
      )}

      <DonationMembersDialog
        batch={openBatch}
        onOpenChange={(open) => {
          if (!open) setOpenBatch(null);
        }}
      />

      <DonationPublishDialog open={publishing} onOpenChange={setPublishing} />

      <ConfirmDialog
        open={confirming !== null}
        onOpenChange={(open) => !open && setConfirming(null)}
        title="Confirm this batch?"
        description={
          confirming === null
            ? ''
            : `Batch #${confirming.legacyId ?? confirming.batchId} moves on to Accounts.`
        }
        confirmLabel="Confirm"
        isPending={confirm.isPending}
        onConfirm={() =>
          confirming && confirm.mutate({ batchId: confirming.batchId, isConfirm: true })
        }
      />

      <ConfirmDialog
        open={disbursing !== null}
        onOpenChange={(open) => !open && setDisbursing(null)}
        title="Disburse this batch?"
        description={
          disbursing === null
            ? ''
            : `Batch #${disbursing.legacyId ?? disbursing.batchId} is marked paid. This cannot be undone.`
        }
        confirmLabel="Disburse"
        isPending={disburse.isPending}
        onConfirm={() => disbursing && disburse.mutate({ batchId: disbursing.batchId })}
      />
    </div>
  );
}
